import { useCallback, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { acquireNexusApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { alerts } from '../../../shared/ui/Alerts';
import { ChatDocument } from '../../../domain/documents/models/ChatDocument';
import {
    setDocuments,
    setIsImporting,
    setSelectedDocuments,
    clearSelectedDocuments,
} from '../../../redux/features/chatDocuments/chatDocumentsSlice';
import { shallowEqual } from 'react-redux';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { NexusAgent } from '../../../domain/agents/models/Agent';
import { UploadStatus } from '../components/UploadStatusIndicator';
import { FileUploadStatus } from './useProGPTDocuments';

export interface UseDocumentsVm {
    documents: ChatDocument[];
    isImporting: number;
    uploadStatus: UploadStatus;
    fileUploadStatuses: Map<string, FileUploadStatus>;
    selectedDocumentIds: Set<string>;
    isDeleting: boolean;
    onUploadDocuments: (files: File[]) => Promise<void>;
    onFetchDocuments: () => Promise<void>;
    onDeleteDocuments: (documentIds: string[]) => Promise<void>;
    onSelectDocument: (documentId: string, selected: boolean) => void;
    onSelectAllDocuments: () => void;
    onClearSelection: () => void;
}

export const useNexusDocuments = (sessionId: string, resolvedAgent: NexusAgent | null): UseDocumentsVm => {
    const dispatch = useAppDispatch();
    const providerServices = useProviderServices();
    const { instance } = useMsal();
    const [isDeleting, setIsDeleting] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');

    const documents = useAppSelector((state) => state.documents.bySessionId[sessionId] ?? [], shallowEqual);
    const isImporting = useAppSelector((state) => state.documents.isImporting[sessionId] ?? 0);
    const selectedDocumentIdsArray = useAppSelector(
        (state) => state.documents.selectedDocumentIds[sessionId] ?? [],
        shallowEqual
    );
    const selectedDocumentIds = useMemo(() => new Set(selectedDocumentIdsArray), [selectedDocumentIdsArray]);

    const onFetchDocuments = useCallback(async (): Promise<void> => {
        try {
            const accessToken = await acquireNexusApiAccessTokenAsync({
                msalInstance: instance,
            });

            const chatSessionResult = await providerServices.nexus.sessions.getChatAsync(
                sessionId,
                accessToken,
            );
            dispatch(setDocuments({ sessionId, documents: chatSessionResult.documents }));
        } catch (error) {
            alerts.error('Failed to fetch documents. Please try again.');
            console.error('Failed to fetch documents:', error);
        }
    }, [dispatch, instance, providerServices.nexus.sessions, sessionId]);

    const onUploadDocuments = useCallback(
        async (files: File[]): Promise<void> => {
            if (files.length === 0) return;

            dispatch(setIsImporting({ sessionId, count: files.length }));
            setUploadStatus('uploading');

            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const updatePayload: any = {
                    providerType: ChatProviderType.Nexus,
                };

                if (resolvedAgent?.allowFileSearchAtConversationLevel) {
                    updatePayload.filesForFileSearch = files;
                    updatePayload.enableFileSearch = true;
                } else if (resolvedAgent?.allowCodeInterpreterAtConversationLevel) {
                    updatePayload.filesForCodeInterpreter = files;
                    updatePayload.enableCodeInterpreter = true;
                } else {
                    // this would never happen because document tab is only shown for agents that have at least one of the capabilities enabled, but we add this fallback just in case
                    alerts.error('Failed to upload documents. Please enable file search or code interpreter at the Edit Agent page and try again.');
                    return;
                }

                await providerServices.nexus.sessionWrite.updateChatAsync(
                    sessionId,
                    updatePayload,
                    accessToken,
                );

                await onFetchDocuments();
                alerts.success('Documents uploaded successfully.');
                setUploadStatus('success');
            } catch (error) {
                alerts.error('Failed to upload documents. Please try again.');
                console.error('Failed to upload documents:', error);
                setUploadStatus('error');
            } finally {
                dispatch(setIsImporting({ sessionId, count: 0 }));
            }
        },
        [dispatch, instance, onFetchDocuments, providerServices.nexus.sessionWrite, resolvedAgent, sessionId],
    );

    const onDeleteDocuments = useCallback(
        async (documentIds: string[]): Promise<void> => {
            if (documentIds.length === 0) return;

            setIsDeleting(true);

            try {
                const accessToken = await acquireNexusApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const fileNamesToDelete = documents
                    .filter((doc) => documentIds.includes(doc.id))
                    .map((doc) => doc.name);

                if (fileNamesToDelete.length === 0) {
                    alerts.error('No valid files selected for deletion.');
                    return;
                }

                const updatePayload: any = {
                    providerType: ChatProviderType.Nexus,
                };

                // Determine which capability to use based on agent configuration
                if (resolvedAgent?.allowFileSearchAtConversationLevel) {
                    updatePayload.filesToDeleteForFileSearch = fileNamesToDelete;
                } else if (resolvedAgent?.allowCodeInterpreterAtConversationLevel) {
                    updatePayload.filesToDeleteForCodeInterpreter = fileNamesToDelete;
                } else {
                    // Default to file search if neither is explicitly set
                    updatePayload.filesToDeleteForFileSearch = fileNamesToDelete;
                }

                await providerServices.nexus.sessionWrite.updateChatAsync(
                    sessionId,
                    updatePayload,
                    accessToken,
                );

                alerts.success('Documents deleted successfully.');
                dispatch(clearSelectedDocuments(sessionId));
                await onFetchDocuments();
            } catch (error) {
                alerts.error('Failed to delete documents. Please try again.');
                console.error('Failed to delete documents:', error);
            } finally {
                setIsDeleting(false);
            }
        },
        [dispatch, instance, onFetchDocuments, providerServices.nexus.sessionWrite, resolvedAgent, sessionId],
    );

    const onSelectDocument = useCallback(
        (documentId: string, selected: boolean): void => {
            const newSelection = new Set(selectedDocumentIds);
            if (selected) {
                newSelection.add(documentId);
            } else {
                newSelection.delete(documentId);
            }
            dispatch(setSelectedDocuments({ sessionId, documentIds: Array.from(newSelection) }));
        },
        [dispatch, selectedDocumentIds, sessionId],
    );

    const onSelectAllDocuments = useCallback((): void => {
        const allIds = documents.filter((doc) => !doc.id.startsWith('in-progress')).map((doc) => doc.id);
        dispatch(setSelectedDocuments({ sessionId, documentIds: allIds }));
    }, [dispatch, documents, sessionId]);

    const onClearSelection = useCallback((): void => {
        dispatch(clearSelectedDocuments(sessionId));
    }, [dispatch, sessionId]);

    // useEffect(() => {
    //     // Only fetch if this is an active Nexus session
    //     if (sessionId && session?.providerType === ChatProviderType.Nexus) {
    //         void onFetchDocuments();
    //     }
    // }, [sessionId]);

    return {
        documents,
        isImporting,
        uploadStatus,
        fileUploadStatuses: new Map<string, FileUploadStatus>(),
        selectedDocumentIds,
        isDeleting,
        onUploadDocuments,
        onFetchDocuments,
        onDeleteDocuments,
        onSelectDocument,
        onSelectAllDocuments,
        onClearSelection,
    };
};
